import { Position } from '@ardoq/component-hierarchies';
import { ContextMenuSourceNode } from '@ardoq/context-menu';
import { getComponentElement } from 'createReferences2024/utils';
import * as canvasHitTestRegistry from 'tabview/canvasRendering/canvasHitTestRegistry';
import { CANVAS_HIT_TEST_ATTRIBUTE } from 'tabview/canvasRendering/canvasHitTestRegistry';

/**
 * Which occurrence of the source component the gesture started from. See
 * `CreateReferenceOverlay.tsx` for why the three surfaces answer differently. A grouped
 * view renders one component several times, so its id cannot say - the element
 * can, and where there is no element the graph's own node id can.
 */
export type SourceHandle =
  | { kind: 'element'; element: Element }
  | { kind: 'canvas'; viewInstanceId: string; nodeId: string }
  /**
   * The navigator answers for its own rows: it knows where every node would be
   * whether or not the row is rendered, so it publishes the box rather than
   * being asked. Nothing here resolves this kind.
   */
  | { kind: 'navigator'; nodeId: string };

/**
 * Where the source component is, and the box it has to be inside to be seen.
 * One value, because the two are only meaningful together: a position carried
 * over from an earlier frame next to a box resolved in this one would be
 * measured against something it was never measured in.
 */
export type SourceBox = {
  position: Position;
  /** `null` when nothing the gesture knows about clips the source. */
  clipBox: DOMRect | null;
};

/**
 * The surface the gesture was armed on, from what the context menu was given.
 * A view that draws rather than renders names the node itself - it has the node
 * in hand and no DOM element to offer, which is why the canvas paths set
 * `target` to `null`.
 */
export const resolveSourceHandle = (
  target: Element | null,
  sourceNode: ContextMenuSourceNode | undefined
): SourceHandle | null => {
  if (sourceNode) return { kind: 'canvas', ...sourceNode };
  const element = getComponentElement(target);
  if (!element) return null;
  const nodeId = toNavigatorNodeId(element);
  return nodeId ? { kind: 'navigator', nodeId } : { kind: 'element', element };
};

/**
 * A navigator row, by the id of the node it stands for - unique per row, where
 * a component id is not. `null` for anything outside the tree.
 */
const toNavigatorNodeId = (element: Element): string | null =>
  element.closest(NAVIGATOR_ROOT_SELECTOR)
    ? element.getAttribute('data-id')
    : null;

/**
 * A `DOMRect` cannot be compared the way a plain object can: its fields are
 * getters on the prototype, so `Object.keys` on one is empty and a structural
 * helper has nothing to look at. `lodash.isEqual` happens to tell two of them
 * apart under jsdom, which says nothing about a browser - so compare the fields
 * that are actually used and let the behaviour be the same everywhere.
 */
export const isSameSourceBox = (
  previous: SourceBox | null,
  current: SourceBox | null
): boolean => {
  if (previous === current) return true;
  if (!previous || !current) return false;
  return (
    previous.position.x === current.position.x &&
    previous.position.y === current.position.y &&
    isSameClipBox(previous.clipBox, current.clipBox)
  );
};

const isSameClipBox = (
  previous: DOMRect | null,
  current: DOMRect | null
): boolean => {
  if (previous === current) return true;
  if (!previous || !current) return false;
  // The four edges the mask reads; width and height follow from them.
  return (
    previous.top === current.top &&
    previous.right === current.right &&
    previous.bottom === current.bottom &&
    previous.left === current.left
  );
};

const toCentre = (element: Element): Position | null => {
  const { left, top, width, height } = element.getBoundingClientRect();
  if (width === 0 && height === 0) return null;
  return { x: left + width / 2, y: top + height / 2 };
};

/**
 * The pane only speaks for a source it holds: a row in the navigator has a pane
 * of its own, and measured against this one it reads as permanently off to the
 * left, which would mask the very stretch of tether drawn over its own visible
 * row. Answering `null` masks nothing instead, which is the safe direction.
 */
const toPaneBox = (
  paneRef: React.RefObject<HTMLDivElement | null> | null,
  element: Element
): DOMRect | null => {
  const pane = paneRef?.current;
  if (!pane || !pane.contains(element)) return null;
  return pane.getBoundingClientRect();
};

/** The navigator's own scrolling pane, which is what clips its rows. */
export const NAVIGATOR_ROOT_SELECTOR = '#navigator-root';

const fromElement = (
  element: Element,
  paneRef: React.RefObject<HTMLDivElement | null> | null
): SourceBox | null => {
  if (!element.isConnected) return null;
  const position = toCentre(element);
  return position && { position, clipBox: toPaneBox(paneRef, element) };
};

/**
 * A canvas view has no element to measure, so it is asked instead - it moves
 * its nodes by drawing them elsewhere, without scrolling anything. Its own
 * canvas is the box the node has to be inside to be seen.
 */
const fromCanvas = ({
  viewInstanceId,
  nodeId,
}: Extract<SourceHandle, { kind: 'canvas' }>): SourceBox | null => {
  const position = canvasHitTestRegistry.get(viewInstanceId)?.locate?.(nodeId);
  if (!position) return null;
  const canvas = document.querySelector(
    `[${CANVAS_HIT_TEST_ATTRIBUTE}="${viewInstanceId}"]`
  );
  return { position, clipBox: canvas?.getBoundingClientRect() ?? null };
};

/**
 * `null` when the source cannot be located at all - the element left the
 * document, the view culled it, or the canvas cannot say where its node is -
 * so the caller keeps its last answer rather than anchoring the tether to the
 * viewport corner.
 */
export const toSourceBox = (
  handle: SourceHandle | null,
  paneRef: React.RefObject<HTMLDivElement | null> | null
): SourceBox | null => {
  switch (handle?.kind) {
    case 'element':
      return fromElement(handle.element, paneRef);
    case 'canvas':
      return fromCanvas(handle);
    // Published by the navigator, so there is nothing to resolve here.
    default:
      return null;
  }
};
